import { useEffect, useCallback } from 'react';
import { Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  GEOLOCATION_ERRORS,
  GEOLOCATION_MAXIMUM_AGE_MS,
  GEOLOCATION_TIMEOUT_MS,
  MAX_ATTEMPTS_LOCATION,
} from '../../../../config/consts/commons';

const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: GEOLOCATION_TIMEOUT_MS,
  maximumAge: GEOLOCATION_MAXIMUM_AGE_MS,
};

function LocationButton({
  setLocation,
  locationStatus,
  setLocationStatus,
  setErrorMessage,
  attempts,
  setAttempts,
}) {
  const { t } = useTranslation(['button', 'system', 'description']);

  const getLocation = useCallback(() => {
    setLocationStatus('loading');
    setErrorMessage('');

    if (navigator.geolocation && attempts < MAX_ATTEMPTS_LOCATION) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ latitude, longitude });
          setLocationStatus('success');
        },
        (error) => {
          console.error('Geolocation error:', error);
          setAttempts((prev) => prev + 1);

          const errorKey = GEOLOCATION_ERRORS[error.code];
          const errorMessage = errorKey
            ? t(errorKey, { ns: 'system' })
            : t('LOCATION_ATTEMPT_FAILED', { ns: 'system' });

          setErrorMessage(errorMessage);
          setLocationStatus('error');
        },
        GEOLOCATION_OPTIONS
      );
    } else {
      setLocationStatus('error');
      setErrorMessage(t('LOCATION_ATTEMPT_FAILED', { ns: 'system' }));
    }
  }, [
    setLocationStatus,
    setErrorMessage,
    attempts,
    setLocation,
    setAttempts,
    t,
  ]);

  useEffect(() => {
    if (locationStatus === 'idle') {
      getLocation();
    }
  }, [getLocation, locationStatus]);

  if (locationStatus === 'success') {
    return (
      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          mb: 1.5,
          fontWeight: 500,
          color: '#2e7d32',
        }}
      >
        {t('orders.location_loaded', { ns: 'description' })} ✅
      </Typography>
    );
  }

  if (locationStatus === 'error' && attempts >= MAX_ATTEMPTS_LOCATION) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      color="primary"
      onClick={getLocation}
      disabled={locationStatus === 'loading'}
      fullWidth
      sx={{
        textTransform: 'uppercase',
        fontWeight: 500,
        mb: 1.5,
      }}
    >
      {locationStatus === 'loading' ? (
        <CircularProgress size={24} color="inherit" />
      ) : (
        <Typography variant="body2">
          {t('get_current_location', { ns: 'button' })}
        </Typography>
      )}
    </Button>
  );
}

export default LocationButton;
